import { supabase } from '@/lib/supabase';
import { Doctor } from '@/types';

/**
 * Find doctor profile by user email (using contact_number column)
 */
export async function findDoctorByEmail(email: string): Promise<Doctor | null> {
  try {
    const { data, error } = await supabase
      .from('doctors')
      .select('*')
      .ilike('contact_number', email)
      .single();

    if (error) {
      console.log('No doctor found with email:', email);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error finding doctor by email:', error);
    return null;
  }
}

/**
 * Find doctor profile by user name
 */
export async function findDoctorByName(name: string): Promise<Doctor | null> {
  try {
    const { data, error } = await supabase
      .from('doctors')
      .select('*')
      .ilike('name', name)
      .single();

    if (error) {
      console.log('No doctor found with name:', name);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error finding doctor by name:', error);
    return null;
  }
}

/**
 * Get doctor ID for current user
 * Since doctors table uses user ID as primary key, we can directly check if user ID exists in doctors table
 */
export async function getDoctorIdForUser(user: any): Promise<string | null> {
  if (!user?.id) return null;

  try {
    // Check if user ID exists in doctors table
    const { data, error } = await supabase
      .from('doctors')
      .select('id')
      .eq('id', user.id)
      .single();

    if (error) {
      console.log('No doctor profile found for user ID:', user.id);
      return null;
    }

    return data.id;
  } catch (error) {
    console.error('Error finding doctor by user ID:', error);
    return null;
  }
}

/**
 * Create a doctor profile for the current user if it doesn't exist
 * Note: This should only be called for users with role 'doctor'
 */
export async function ensureDoctorProfile(user: any): Promise<string | null> {
  if (!user?.id) return null;

  // Check if doctor profile already exists
  let doctorId = await getDoctorIdForUser(user);
  
  if (doctorId) {
    return doctorId;
  }

  // Create a new doctor profile using the user ID as the primary key
  try {
    const { data, error } = await supabase
      .from('doctors')
      .insert([{
        id: user.id, // Use user ID as primary key
        name: user.name || user.user_metadata?.name || 'Unknown Doctor',
        contact_number: user.email,
        specialization: 'General Medicine',
        registration_number: 'TEMP-' + Date.now(),
        status: 'available',
        active: true,
        available: false,
        patients_today: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating doctor profile:', error);
      return null;
    }

    console.log('Created doctor profile:', data);
    return data.id;
  } catch (error) {
    console.error('Error in ensureDoctorProfile:', error);
    return null;
  }
}
